import { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useQuery } from '@tanstack/react-query';
import { Alert, MenuItem, TextField } from '@mui/material';
import api from '../../services/api';
import { listTenants } from '../../services/tenantService';
import { selectIsSuperAdmin } from '../../store/slices/authSlice';
import { selectTenantSlug } from '../../store/slices/tenantSlice';
import { useTenantCurrency } from '../../hooks/useTenantCurrency';
import { formatMoney } from '../../utils/currency';

export type AccountType = 'user' | 'driver' | 'provider';
export type RewardType = 'wallet' | 'points';
export type ReferralTrigger = 'signup' | 'first_completion' | 'n_completions';

export const ACCOUNT_TYPES: AccountType[] = ['user', 'driver', 'provider'];

export const ACCOUNT_LABELS: Record<AccountType, string> = {
  user: 'Customer',
  driver: 'Driver / Partner',
  provider: 'Service Provider',
};

export const SERVICE_LABELS: Record<string, string> = {
  ride: 'Ride',
  rental: 'Rental',
  outstation: 'Outstation',
  porter: 'Porter / Delivery',
  food: 'Food',
  grocery: 'Grocery',
  home_service: 'Home services',
};

export const TRIGGER_LABELS: Record<ReferralTrigger, string> = {
  signup: 'On signup / approval',
  first_completion: 'After first completed trip/job',
  n_completions: 'After N completed trips/jobs',
};

export const STATUS_COLORS: Record<string, 'default' | 'success' | 'warning' | 'error' | 'info'> = {
  pending: 'warning',
  processing: 'info',
  completed: 'success',
  failed: 'error',
  expired: 'default',
  rejected: 'error',
};

export interface ServiceAmount {
  service: string;
  referrerReward: number;
  refereeReward: number;
}

export interface ReferralRule {
  referrerType: AccountType;
  refereeType: AccountType;
  enabled: boolean;
  referrerReward: number;
  refereeReward: number;
  rewardType: RewardType;
  trigger: ReferralTrigger;
  requiredCompletions: number;
  minOrderAmount: number;
  services: string[];
  serviceAmounts: ServiceAmount[];
  requireApproval: boolean;
  validityDays: number;
  maxPerMonth: number;
  maxLifetime: number;
}

export interface ReferralConfig {
  enabled: boolean;
  allowLateApply: boolean;
  lateApplyDays: number;
  preventSameDevice: boolean;
  shareMessage: string;
  terms: string[];
  rules: ReferralRule[];
}

export interface PointsEarnRule {
  service: string;
  enabled: boolean;
  pointsPerUnit: number;
  perAmount: number;
  fixedPoints: number;
  minAmount: number;
}

export interface PointsAccountConfig {
  enabled: boolean;
  rules: PointsEarnRule[];
}

export interface PointsTier {
  name: string;
  minPoints: number;
  multiplier: number;
}

export interface PointsConversion {
  enabled: boolean;
  pointsPerUnit: number;
  minPoints: number;
  maxPointsPerDay: number;
  maxPointsPerMonth: number;
  allowManual: boolean;
  autoConvert: boolean;
  autoThreshold: number;
}

export interface PointsConfig {
  enabled: boolean;
  accounts: Record<AccountType, PointsAccountConfig>;
  tiersEnabled: boolean;
  tiers: PointsTier[];
  expiryDays: number;
  conversion: PointsConversion;
}

export interface RewardsConfigResponse {
  referral: ReferralConfig;
  points: PointsConfig;
  referralConfigured: boolean;
  pointsConfigured: boolean;
  meta: {
    accountTypes: AccountType[];
    services: string[];
    triggers: ReferralTrigger[];
    rewardTypes: RewardType[];
    statuses: string[];
    currency: { code: string; symbol: string };
    referralDefaults?: { shareMessage: string; terms: string[] };
  };
}

export interface PersonView {
  _id?: string;
  name?: string;
  phone?: string;
  email?: string;
  userType?: string;
  accountType?: AccountType | null;
  referralCode?: string;
}

export interface PaginatedResult<T> {
  rows: T[];
  total: number;
}

export function personLabel(p?: PersonView | null) {
  if (!p) return '—';
  return p.name || p.phone || p.email || '—';
}

export function titleCase(value?: string) {
  if (!value) return '—';
  return value.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

export function dateText(value?: string) {
  return value ? new Date(value).toLocaleString() : '—';
}

/**
 * Currency for rewards screens: the config's `meta.currency` when loaded, otherwise the
 * tenant currency. Never falls back to a hardcoded symbol.
 */
export function useRewardsCurrency(currency?: { code?: string; symbol?: string } | null) {
  const tenantCurrency = useTenantCurrency();
  const code = currency?.code || tenantCurrency.currency || undefined;
  const symbol = currency?.symbol || tenantCurrency.symbol || '';
  const money = useCallback(
    (value: unknown) => formatMoney(value, { symbol: symbol || undefined, currency: code }),
    [symbol, code],
  );
  return { code, symbol, money };
}

/**
 * Super admins may pick a tenant explicitly (sent as X-Tenant-Id); otherwise the
 * global tenant switcher context applies. Client admins are always scoped to their tenant.
 */
export function useRewardsTenant() {
  const isSuperAdmin = useSelector(selectIsSuperAdmin);
  const storeSlug = useSelector(selectTenantSlug);
  const globalSlug = localStorage.getItem('tenantSlug') ? storeSlug : '';
  const [tenantId, setTenantId] = useState('');

  const { data: tenants } = useQuery({
    queryKey: ['rewards-tenants'],
    queryFn: () => listTenants({ limit: 200, page: 1 }),
    enabled: isSuperAdmin,
  });

  const headers = useMemo(() => {
    const out: Record<string, string> = {};
    if (isSuperAdmin && tenantId) out['X-Tenant-Id'] = tenantId;
    return out;
  }, [isSuperAdmin, tenantId]);
  const ready = !isSuperAdmin || Boolean(tenantId) || Boolean(globalSlug);
  const scopeKey = isSuperAdmin ? tenantId || globalSlug || 'none' : 'own';

  const picker = isSuperAdmin ? (
    <TextField
      select
      size="small"
      label="Tenant"
      value={tenantId}
      onChange={(e) => setTenantId(e.target.value)}
      sx={{ minWidth: 220 }}
    >
      <MenuItem value="">{globalSlug ? `Current context (${globalSlug})` : 'Select tenant'}</MenuItem>
      {(tenants?.data || []).map((t) => (
        <MenuItem key={t._id} value={t._id}>{t.companyName}</MenuItem>
      ))}
    </TextField>
  ) : null;

  const notReady = ready ? null : (
    <Alert severity="info" sx={{ mb: 2 }}>Select a tenant to manage its referral and points programs.</Alert>
  );

  return { isSuperAdmin, headers, ready, scopeKey, picker, notReady };
}

export async function fetchRewardsConfig(headers: Record<string, string>) {
  const res = await api.get('/admin/rewards/config', { headers });
  return res.data.data as RewardsConfigResponse;
}

/** Reads `sendPaginated` responses: `{ data, meta: { total, page, limit, totalPages } }`. */
export async function fetchPaginated<T>(
  url: string,
  params: Record<string, unknown>,
  headers: Record<string, string>,
): Promise<PaginatedResult<T>> {
  const res = await api.get(url, { params, headers });
  const body = res.data as { data?: T[]; meta?: { total?: number } };
  return {
    rows: Array.isArray(body?.data) ? body.data : [],
    total: Number(body?.meta?.total ?? 0),
  };
}
